import { useSelector, useDispatch } from 'react-redux';
import EntityHelper from '../../../storage/classes/Entity';
import {
	IEntityHelper,
	EntityHelperOpts,
	entityHelperDefaultOpts
} from '../../../storage';
import {
	getUserCollection,
	UserId,
	UserIds,
	UserId_Some,
	UserEntity,
	UserEntities,
	UserEntity_Some,
	UserEntityPatch_Some,
	UserCollection,
	UserCollectionState,
	IUserActions,
	userActions,
	UserActionTypes
} from '..';
import {
	readUsers,
	writeUsers,
	ReadUsersRequest,
	WriteUsersRequest,
	UserApiOperation,
	readUsersAll,
	GetUserByIdRequest,
	getUserById
} from '../apis';
import { UseCtx } from '../../../config/hooks';
import {
	ContextEntities,
	ContextEntity,
	EventEntities,
	ServiceEntities,
	ServiceEntity
} from '../collections';
import moment from 'moment';
import _ from 'lodash';
import { isObjectStatusActive } from '../models';
import { Utils } from '../../../app';

/**
 * User helper interface
 *
 * @export
 * @interface IUserHelper
 * @extends {IEntityHelper}
 */
export interface IUserHelper extends IEntityHelper {
	// customProperty: any;
	// customMethod(): any;
	// Custom functions
}

/**
 * User helper options interface
 *
 * @export
 * @interface UserHelperOpts
 * @extends {EntityHelperOpts}
 */
export interface UserHelperOpts extends EntityHelperOpts {
	// customOpt: any;
}

const userHelperOpts: UserHelperOpts = {
	...entityHelperDefaultOpts,
	...{}
};

export interface UserEntityFilter {
	keyWords?: string;
	contextIds?: string[];
}

/**
 * User helper
 *
 * @export
 * @class UserHelper
 * @extends {EntityHelper<UserCollection, UserActionTypes, UserActions, UserEntity, UserEntities, UserEntity_Some, UserEntityPatch_Some, UserId, UserIds, UserId_Some, UserCollectionState, UserHelperOpts>}
 * @implements {IUserHelper}
 */
export class UserHelper
	extends EntityHelper<
		UserCollection,
		UserActionTypes,
		IUserActions,
		UserEntity,
		UserEntities,
		UserEntity_Some,
		UserEntityPatch_Some,
		UserId,
		UserIds,
		UserId_Some,
		UserCollectionState,
		UserHelperOpts
	>
	implements IUserHelper {
	constructor() {
		super(
			useSelector(getUserCollection),
			userActions,
			useDispatch(),
			userHelperOpts
		);
		this.collection = useSelector(getUserCollection);
		this.dispatch = useDispatch();
	}

	lastSuccess(operationId: UserApiOperation, requestId: string = 'default') {
		return this.filter(
			entity =>
				!!entity.__state?.api?.operations?.[operationId]?.[requestId]?.success
					?.last?.dt
		).reverse()[0]?.__state?.api?.operations?.[operationId]?.[requestId]
			?.success?.last?.dt;
	}

	async readUserById(
		ctx: UseCtx<any>,
		params: GetUserByIdRequest,
		callback?: any
	): Promise<UserEntity | undefined> {
		return (await getUserById(ctx, params)
			.then((entity: UserEntity | undefined) => {
				if (callback) callback(entity);
				return entity;
			})
			.catch(e => {
				if (callback) callback(e);
				return;
			})) as UserEntity | undefined;
	}

	async read(
		ctx: UseCtx<any>,
		params: Partial<ReadUsersRequest> = {},
		callback?: any
	): Promise<UserEntities> {
		if (!ctx.app.user.active()?.userId) return [];
		//params.modifiedFrom =
		//	params.modifiedFrom || this.lastSuccess(UserApiOperation.readUsers);

		let request: ReadUsersRequest = {
			...params,
			...{
				userId: ctx.app.user.active()?.userId || ''
			}
		};
		if (request.userId === '') return [];

		let entities: UserEntities = await readUsers(ctx, request)
			.then((entities: UserEntities) => {
				if (callback) callback(entities);
				return entities;
			})
			.catch(e => {
				if (callback) callback(e);
				return [];
			});
		return entities;
	}

	async readAll(
		ctx: UseCtx<any>,
		params: Partial<ReadUsersRequest> = {},
		callback?: any
	): Promise<UserEntities> {
		if (!ctx.app.user.active()?.userId) return [];
		//params.modifiedFrom =
		//	params.modifiedFrom || this.lastSuccess(UserApiOperation.readUsersAll);

		let request: ReadUsersRequest = {
			...params,
			...{
				userId: ctx.app.user.active()?.userId || ''
			}
		};
		if (request.userId === '') return [];

		let entities: UserEntities = await readUsersAll(ctx, request)
			.then((entities: UserEntities) => {
				if (callback) callback(entities);
				return entities;
			})
			.catch(e => {
				if (callback) callback(e);
				return [];
			});
		return entities;
	}

	async write(
		ctx: UseCtx<any>,
		params: Partial<WriteUsersRequest> = {},
		callback?: any
	): Promise<UserEntities> {
		if (!ctx.app.user.active()?.userId) return [];
		if (!params.users) return [];
		let request: WriteUsersRequest = {
			...params,
			...{
				users: params.users,
				userId: ctx.app.user.active()?.userId || ''
			}
		};
		if (request.userId === '') return [];

		let entities: UserEntities = await writeUsers(ctx, request)
			.then((entities: UserEntities) => {
				if (callback) callback();
				return entities;
			})
			.catch(e => {
				if (callback) callback(e);
				return [];
			});
		return entities;
	}

	allByFilter(ctx: UseCtx<any>, filter: UserEntityFilter): UserEntities {
		return this.all()
			.filter(isObjectStatusActive)
			.filter(
				user =>
					ctx.lead.serviceHelper
						.allByContextIds(filter.contextIds || [])
						.findIndex(e => e.userIds?.includes(user.id)) >= 0
			)
			.filter(user => {
				let match = 0,
					matches = 0,
					keywords = filter.keyWords ?? '';

				if (!user) {
					return false;
				}

				for (let keyword of keywords.toLowerCase().split(' ')) {
					if (keyword === '') break;
					match++;
					if (
						(user.firstName &&
							user.firstName?.toLowerCase().trim().indexOf(keyword) > -1) ||
						(user.lastName &&
							user.lastName?.toLowerCase().trim().indexOf(keyword) > -1) ||
						(user.email &&
							user.email?.toLowerCase().trim().indexOf(keyword) > -1)
					) {
						matches++;
					}
				}
				return match === matches;
			});
	}

	allByService(service: ServiceEntity): UserEntities {
		return this.all()
			.filter(user => service.userIds?.includes(user.id))
			.filter(isObjectStatusActive);
	}

	async readActiveData(ctx: UseCtx<any>, callback?: any) {
		let userId: string = ctx.app.user.active()?.userId || '';
		if (
			userId !== '' &&
			!ctx.app.user.active()?.security.includes('administrator')
		) {
			ctx.lead.service.readAll(
				ctx,
				{ userIds: [userId] },
				(services: ServiceEntities) => {
					//let [services, err] = Utils.result<ServiceEntities>(result);
					//if (err) return ctx.app.error(err);

					let contextIds: string[] = [],
						eventIds: string[] = [];

					// set default service for user
					ctx.lead.service.setDefault(ctx, services);

					if (services) {
						services.forEach(service => {
							contextIds = _.union(contextIds, service.contextIds || []);
						});
						if (contextIds.length > 0) {
							console.log(contextIds);
							ctx.lead.context.read(
								ctx,
								{ ids: contextIds },
								(contexts: ContextEntities) => {
									ctx.lead.context.setServiceDefault(ctx, contexts);
									console.log('Service data loaded: Contexts from users');

									contexts.forEach(contexts => {
										eventIds = _.union(eventIds, contexts.eventIds || []);
									});
									if (eventIds.length > 0) {
										ctx.lead.event.read(
											ctx,
											{ ids: eventIds },
											(events: EventEntities) => {
												ctx.lead.event.setServiceDefault(ctx, events);
												console.log('Service data loaded: Events from users');
											}
										);
									}
								}
							);
						}

						console.log('User data loaded: Services from users');

						if (callback) callback();
					}
				}
			);
			if (callback) callback();
		} else {
			if (callback) callback();
		}
	}

	hasAccess(user: UserEntity): boolean {
		return true;
	}

	async sendInviteEmail(
		ctx: UseCtx<any>,
		params: {
			to: string;
			contextId: string;
			serviceId: string;
			eventName?: string;
			cc?: string;
			bcc?: string;
		},
		onError?: (message: string) => void,
		onSuccess?: (result: any) => void
	): Promise<any> {
		const { to, contextId, serviceId, eventName, cc, bcc } = params;

		const manageUrl = `https://leads.conexsys.com/activate?serviceId=${serviceId}&contextId=${contextId}`;
		const safeEventName = eventName || 'your event';

		const html = `<div style="background:#f5f7fa;padding:0;margin:0;font-family:'ProximaNova',Arial,sans-serif;">
  <table width="100%" cellpadding="0" cellspacing="0" style="margin:0;padding:0;border-collapse:collapse;">
    <tr>
      <td align="center" style="padding:24px;">
        <table width="600" cellpadding="0" cellspacing="0" style="background:#ffffff;border-radius:8px;overflow:hidden;border:1px solid #e2e8f0;">
          <tr>
            <td style="padding:0;">
              <img src="https://my.conexsys.com/clients/ui/img/mail/clo-email-header.png"
                   alt="CONEXSYS Lead Retrieval"
                   style="display:block;width:100%;max-width:600px;height:auto;" />
            </td>
          </tr>
          <tr>
            <td style="padding:32px;">
              <h1 style="margin:0 0 12px 0;font-size:24px;font-weight:600;color:#111827;">
                Invitation to Manage Leads for ${safeEventName}
              </h1>
              <p style="margin:0 0 16px 0;color:#374151;font-size:15px;line-height:1.5;">
                You have been granted access to the CONEXSYS Lead Retrieval portal for <strong>${safeEventName}</strong>.
              </p>
              <p style="margin:0 0 20px 0;color:#374151;font-size:15px;line-height:1.5;">
                Activate your access to view real‑time leads, manage team usage, and configure optional lead qualifiers.
              </p>
              <p style="text-align:center;margin:0 0 28px 0;">
                <a href="${manageUrl}" target="_blank"
                   style="display:inline-block;background:#2563eb;color:#ffffff;text-decoration:none;
                          font-weight:600;padding:14px 28px;font-size:15px;border-radius:6px;">
                  Activate Access & Manage Leads
                </a>
              </p>
              <div style="margin:0 0 24px 0;">
                <h2 style="margin:0 0 12px 0;font-size:18px;color:#111827;">What You Can Do</h2>
                <ul style="margin:0;padding-left:18px;color:#374151;font-size:14px;line-height:1.6;">
                  <li>Monitor and export captured leads</li>
                  <li>Invite or manage team users (if permitted)</li>
                  <li>Review device / license usage</li>
                  <li>Configure qualifiers and follow‑up options</li>
                </ul>
              </div>
              <h2 style="margin:0 0 12px 0;font-size:18px;color:#111827;">Getting Started</h2>
              <ol style="margin:0 0 24px 0;padding-left:20px;color:#374151;font-size:14px;line-height:1.6;">
                <li>Click “Activate Access & Manage Leads”.</li>
                <li>Create an account or sign in using this email address.</li>
                <li>Access the dashboard to view live and historical leads.</li>
                <li>Configure qualifiers (optional) before the event begins.</li>
              </ol>
              <p style="margin:0 0 32px 0;color:#6b7280;font-size:13px;line-height:1.5;">
                Trouble with the button? Copy and paste this link into your browser:<br>
                <a href="${manageUrl}" target="_blank" style="color:#2563eb;text-decoration:none;">${manageUrl}</a>
              </p>
              <hr style="border:none;border-top:1px solid #e5e7eb;margin:0 0 24px 0;">
              <p style="margin:0 0 8px 0;color:#6b7280;font-size:12px;text-align:center;">
                If you did not expect this email, please contact <a href="https://conexsys.com/lead-support" target="_blank" style="color:#2563eb;text-decoration:none;">CONEXSYS Support</a>.
              </p>
              <p style="margin:0;color:#9ca3af;font-size:11px;text-align:center;">
                © ${new Date().getFullYear()} CONEXSYS. All rights reserved.
              </p>
            </td>
          </tr>
        </table>
      </td>
    </tr>
  </table>
</div>`;

		try {
			const response = await ctx.app.api.sendEmail({
				from: 'CONEXSYS Lead Retrieval <no-reply@conexsysleads.com>',
				sender: 'no-reply@conexsysleads.com',
				to,
				cc,
				bcc,
				subject: `${safeEventName} - Lead Management Invitation`,
				html
			});

			if (!response) throw 'Response missing from call';

			if (response.status < 200 || response.status >= 300) {
				onError && onError(`${response.status}: Email Send Invalid Response`);
			} else {
				onSuccess && onSuccess(response.data);
			}
			return response.data;
		} catch (e) {
			console.error(e);
			onError && onError(`Error attempting to send email: ${e?.message || e}`);
		}
	}
}
